/**
 * Vouchers API client
 *
 * Phase 4-3 / Stream 4 / Plan §12 / Design §4.4 C3 免单券管理.
 *
 * 包装 axios apiClient 调用 admin-service /vouchers 端点。
 * 写动作 (grant/revoke) 通过 client.ts 自动注入 Idempotency-Key。
 *
 * revoke 是两段式 "先通知后作废"：stage 1 立即 flip pending_revoke + 自动触发
 * voucher-revoked notify；24h 后 content-pipeline cron 完成 stage 2 (flip
 * 到 revoked)。前端 revokeScheduledAt 字段表示 cron 预期执行时间。
 */
import { apiClient } from './client';

export type VoucherStatus = 'available' | 'used' | 'pending_revoke' | 'revoked' | 'expired';

export interface VoucherDto {
  voucherId: string;
  userId: string;
  source: string;
  voucherType: string;
  status: VoucherStatus;
  earnedAt: number;
  usedAt: number | null;
  appliedToWorkId: string | null;
  expireAt: number | null;
  grantedBy: string | null;
  revokeScheduledAt: number | null;
  revokeReason: string | null;
  lastActorId: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface ListVouchersFilter {
  status?: VoucherStatus;
  userId?: string;
  voucherType?: string;
  expired?: boolean;
}

export interface ListVouchersResponse {
  items: VoucherDto[];
  total: number;
}

export interface GrantVouchersInput {
  userIds: string[];
  expireAt: number;
  reason: string;
}

export interface GrantVouchersResponse {
  voucherIds: string[];
}

export interface RevokeVoucherResponse {
  voucherId: string;
  status: 'pending_revoke';
  revokeScheduledAt: number;
}

export interface StatusDistributionResponse {
  items: { status: VoucherStatus; count: number }[];
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

export async function listVouchers(
  filter: ListVouchersFilter = {},
): Promise<ListVouchersResponse> {
  const params: Record<string, string> = {};
  if (filter.status) params.status = filter.status;
  if (filter.userId) params.userId = filter.userId;
  if (filter.voucherType) params.voucherType = filter.voucherType;
  if (filter.expired) params.expired = 'true';
  const resp = await apiClient.get<ServiceResponse<ListVouchersResponse>>('/vouchers', { params });
  return resp.data.data;
}

export async function getVoucher(voucherId: string): Promise<VoucherDto> {
  const resp = await apiClient.get<ServiceResponse<VoucherDto>>(
    `/vouchers/${encodeURIComponent(voucherId)}`,
  );
  return resp.data.data;
}

export async function getStatusDistribution(): Promise<StatusDistributionResponse> {
  const resp = await apiClient.get<ServiceResponse<StatusDistributionResponse>>(
    '/vouchers/status-distribution',
  );
  return resp.data.data;
}

export async function grantVouchers(input: GrantVouchersInput): Promise<GrantVouchersResponse> {
  const resp = await apiClient.post<ServiceResponse<GrantVouchersResponse>>(
    '/vouchers/grant', input,
  );
  return resp.data.data;
}

/**
 * 撤销（先通知后作废）：stage 1 立即 pending_revoke + 触发 voucher-revoked notify；
 * stage 2 cron 24h 后 sweep flip 到 revoked。
 */
export async function revokeVoucher(
  voucherId: string, reason: string,
): Promise<RevokeVoucherResponse> {
  const resp = await apiClient.post<ServiceResponse<RevokeVoucherResponse>>(
    `/vouchers/${encodeURIComponent(voucherId)}/revoke`,
    { reason },
  );
  return resp.data.data;
}
